import { useQuery } from '@tanstack/react-query';
import {
  Shield,
  Desktop,
  Warning,
  CheckCircle,
  XCircle,
  CircleNotch,
  LockKey,
} from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';

// ── Types ─────────────────────────────────────────────────────────────────────

interface SecuritySummary {
  activeSessionCount: number;
  failedLoginsLast24h: number;
  successfulLoginsLast24h: number;
  failedLoginRate: number;
}

interface SecurityEvent {
  id: string;
  action: string;
  actorId?: string;
  actorEmail?: string;
  chambersId?: string;
  ip?: string;
  userAgent?: string;
  createdAt: string;
  chambers?: { id: string; name: string; slug: string } | null;
}

interface SecurityOverview {
  summary: SecuritySummary;
  failedByDay: Record<string, number>;
  recentEvents: SecurityEvent[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(str: string) {
  return new Date(str).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function actionColour(action: string) {
  if (action === 'login') return 'text-plat-success-text';
  if (action === 'failed_login') return 'text-plat-danger-text';
  if (action === 'logout') return 'text-plat-muted';
  return 'text-plat-secondary';
}

function actionLabel(action: string) {
  if (action === 'login') return 'Successful login';
  if (action === 'failed_login') return 'Failed login attempt';
  if (action === 'logout') return 'Logout';
  return action;
}

function actionIcon(action: string) {
  if (action === 'login') return <CheckCircle className="h-4 w-4 text-plat-success-text" weight="fill" />;
  if (action === 'failed_login') return <XCircle className="h-4 w-4 text-plat-danger-text" weight="fill" />;
  return <Shield className="h-4 w-4 text-plat-muted" />;
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  icon,
  accent = 'text-plat-info-text',
  sub,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  accent?: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-plat-muted">{label}</div>
          <div className="mt-2 text-[28px] font-bold leading-none text-plat-fg">{value}</div>
          {sub && <div className="mt-1.5 text-xs text-plat-muted">{sub}</div>}
        </div>
        <div className={`${accent} mt-1`}>{icon}</div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function PlatformSecurityPage() {
  const { data, isLoading, error } = useQuery<SecurityOverview>({
    queryKey: ['platform', 'security', 'overview'],
    queryFn: async () => {
      const res = await platformApi.get('/security/overview');
      return res.data?.data ?? res.data;
    },
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft p-5 text-sm text-plat-danger-text">
        {getPlatformApiErrorMessage(error ?? new Error('Failed to load'))}
      </div>
    );
  }

  const { summary, recentEvents, failedByDay } = data;
  const loginEvents = recentEvents.filter((e) => e.action !== 'logout');
  const failedDays = Object.entries(failedByDay).sort(([a], [b]) => a.localeCompare(b));
  const failedMax = Math.max(1, ...failedDays.map(([, n]) => n));
  const failedTotal = failedDays.reduce((s, [, n]) => s + n, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-plat-fg">Security</h2>
          <p className="mt-0.5 text-sm text-plat-muted">
            Real-time authentication and session activity · auto-refreshes every 30s
          </p>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-plat-info-soft">
          <LockKey className="h-5 w-5 text-plat-info-text" weight="fill" />
        </div>
      </div>

      {/* High failed-login rate alert */}
      {summary.failedLoginRate >= 30 && (
        <div className="flex items-center gap-3 rounded-xl border border-plat-danger-line bg-plat-danger-soft px-5 py-3.5">
          <Warning className="h-5 w-5 shrink-0 text-plat-danger-text" weight="fill" />
          <span className="text-sm font-medium text-plat-danger-text">
            High failed login rate detected — {summary.failedLoginRate}% of attempts in the last 24 hours failed. Review the event feed below.
          </span>
        </div>
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Active Sessions"
          value={summary.activeSessionCount.toLocaleString()}
          icon={<Desktop className="h-6 w-6" />}
          accent="text-plat-info-text"
          sub="across all chambers"
        />
        <StatCard
          label="Logins (24h)"
          value={summary.successfulLoginsLast24h}
          icon={<CheckCircle className="h-6 w-6" weight="fill" />}
          accent="text-plat-success-text"
          sub="successful"
        />
        <StatCard
          label="Failed Logins (24h)"
          value={summary.failedLoginsLast24h}
          icon={<XCircle className="h-6 w-6" weight="fill" />}
          accent={summary.failedLoginsLast24h > 10 ? 'text-plat-danger-text' : 'text-plat-muted'}
          sub="login failures"
        />
        <StatCard
          label="Failure Rate (24h)"
          value={`${summary.failedLoginRate}%`}
          icon={<Warning className="h-6 w-6" weight="fill" />}
          accent={summary.failedLoginRate >= 30 ? 'text-plat-danger-text' : summary.failedLoginRate >= 10 ? 'text-plat-warning-text' : 'text-plat-muted'}
          sub="of all attempts"
        />
      </div>

      {/* Failed-login trend */}
      {failedDays.length > 0 && (
        <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-plat-fg">Failed logins — last {failedDays.length} days</h3>
            <span className="text-xs text-plat-faint">{failedTotal} total</span>
          </div>
          <div className="flex items-end gap-1.5" style={{ height: 96 }}>
            {failedDays.map(([day, n]) => (
              <div key={day} className="group flex flex-1 flex-col items-center justify-end" title={`${day}: ${n} failed`}>
                <div
                  className={`w-full rounded-t transition ${n > 0 ? 'bg-plat-chart-danger group-hover:bg-plat-danger-text' : 'bg-plat-chart-track'}`}
                  style={{ height: `${(n / failedMax) * 100}%`, minHeight: n > 0 ? 3 : 1 }}
                />
              </div>
            ))}
          </div>
          <div className="mt-2 flex justify-between text-[10px] text-plat-faint">
            <span>{failedDays[0]?.[0]}</span>
            <span>{failedDays[failedDays.length - 1]?.[0]}</span>
          </div>
        </div>
      )}

      {/* Event feed */}
      <div className="rounded-xl border border-plat-border bg-plat-surface">
        <div className="flex items-center gap-2 border-b border-plat-border px-5 py-4">
          <Shield className="h-4 w-4 text-plat-muted" weight="fill" />
          <h3 className="text-sm font-semibold text-plat-fg">
            Recent auth events
          </h3>
          <span className="ml-auto text-xs text-plat-faint">
            {loginEvents.length} events shown
          </span>
        </div>

        {loginEvents.length === 0 ? (
          <div className="py-12 text-center text-sm text-plat-muted">
            No auth events recorded yet.
          </div>
        ) : (
          <div className="divide-y divide-plat-border">
            {loginEvents.map((event) => (
              <div key={event.id} className="flex items-center gap-4 px-5 py-3 transition hover:bg-plat-hover">
                <div className="shrink-0">
                  {actionIcon(event.action)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${actionColour(event.action)}`}>
                      {actionLabel(event.action)}
                    </span>
                    {event.actorEmail && (
                      <span className="text-xs text-plat-secondary">{event.actorEmail}</span>
                    )}
                  </div>
                  <div className="mt-0.5 flex items-center gap-3 flex-wrap">
                    {event.chambers && (
                      <span className="text-xs text-plat-muted">
                        {event.chambers.name}
                      </span>
                    )}
                    {event.ip && (
                      <span className="text-xs text-plat-faint">{event.ip}</span>
                    )}
                    {event.userAgent && (
                      <span
                        className="max-w-[220px] truncate text-[10px] text-plat-secondary"
                        title={event.userAgent}
                      >
                        {event.userAgent.split(' ').slice(0, 3).join(' ')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-xs text-plat-muted">{fmt(event.createdAt)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
